import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, RotateCw } from 'lucide-react';
import api from '../api';
import Swal from 'sweetalert2';

export default function CreateTaskModal({ isOpen, onClose, task, employees = [] }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    assigned_to: '',
    title: '',
    description: '',
    priority: 'medium',
    category: '',
    start_date: '',
    due_date: '',
    estimated_hours: ''
  });

  useEffect(() => {
    if (task) {
      setFormData({
        assigned_to: task.assigned_to || '',
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        category: task.category || '',
        start_date: task.start_date ? task.start_date.split('T')[0] : '',
        due_date: task.due_date ? task.due_date.split('T')[0] : '',
        estimated_hours: task.estimated_hours || ''
      });
    } else {
      setFormData({
        assigned_to: '',
        title: '',
        description: '',
        priority: 'medium',
        category: '',
        start_date: new Date().toISOString().split('T')[0],
        due_date: '',
        estimated_hours: ''
      });
    }
  }, [task, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.assigned_to || !formData.title || !formData.due_date) {
      Swal.fire('Validation Error', 'Please fill in all required fields.', 'warning');
      return;
    }

    try {
      setLoading(true);
      if (task) {
        await api.put(`/admin/tasks/${task.id}`, formData);
        Swal.fire('Success', 'Task updated successfully', 'success');
      } else {
        await api.post('/admin/tasks', formData);
        Swal.fire('Success', 'New task created successfully', 'success');
      }
      onClose();
    } catch (err) {
      console.error("Error saving task:", err);
      const errorMsg = err.response?.data?.message || 'Failed to save task';
      const validationErrors = err.response?.data?.errors;
      
      let detailedError = errorMsg;
      if (validationErrors) {
        detailedError = Object.values(validationErrors).flat().join('\n');
      }
      
      Swal.fire('Error', detailedError, 'error');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md transition-opacity" 
        onClick={onClose}
      ></div>
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[500px] flex flex-col max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between ${task ? 'bg-blue-600' : 'bg-purple-600'}`}>
          <h2 className="text-lg font-black text-white tracking-wide">
            {task ? 'Edit Task' : 'Create New Task'}
          </h2>
          <button 
            onClick={onClose}
            className="p-1 text-white/70 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-slate-50/30">
          <form id="task-form" onSubmit={handleSubmit} className="space-y-4">
            
            <div className="space-y-1.5">
              <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wider">Assign To <span className="text-red-500">*</span></label>
              <select 
                value={formData.assigned_to}
                onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}
                className="w-full border border-slate-200 rounded-lg text-[13px] font-medium text-slate-700 py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-colors shadow-sm appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M6%209L12%2015L18%209%22%20stroke%3D%22%2364748B%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[center_right_10px]"
              >
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wider">Task Title <span className="text-red-500">*</span></label>
              <input 
                type="text" 
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Enter task title" 
                className="w-full border border-slate-200 rounded-lg text-[13px] font-medium text-slate-700 py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-colors shadow-sm font-bold"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wider">Description</label>
              <textarea 
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Enter task details..." 
                className="w-full border border-slate-200 rounded-lg text-[13px] font-medium text-slate-700 py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-colors shadow-sm resize-y"
              ></textarea>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wider">Priority <span className="text-red-500">*</span></label>
                <select 
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg text-[13px] font-medium text-slate-700 py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-colors shadow-sm appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M6%209L12%2015L18%209%22%20stroke%3D%22%2364748B%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[center_right_10px]"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wider">Category</label>
                <input 
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value.toLowerCase()})}
                  placeholder="e.g. Admission" 
                  className="w-full border border-slate-200 rounded-lg text-[13px] font-medium text-slate-700 py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-colors shadow-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wider">Start Date</label>
                <input 
                  type="date" 
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg text-[12px] font-bold text-slate-700 py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-colors shadow-sm"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wider">Due Date <span className="text-red-500">*</span></label>
                <input 
                  type="date" 
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg text-[12px] font-bold text-slate-700 py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-colors shadow-sm"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wider">Est. Hours</label>
              <input 
                type="number" 
                value={formData.estimated_hours}
                onChange={(e) => setFormData({...formData, estimated_hours: e.target.value})}
                placeholder="e.g. 4" 
                className="w-full border border-slate-200 rounded-lg text-[13px] font-bold text-slate-700 py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-colors shadow-sm"
              />
            </div>

          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-white border-t border-slate-100 flex items-center justify-between gap-3 z-10">
          <button 
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 font-bold text-[13px] text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-sm uppercase tracking-wide"
          >
            Cancel
          </button>
          <button 
            type="submit"
            form="task-form"
            disabled={loading}
            className={`flex-1 px-4 py-2.5 font-black text-[13px] flex justify-center items-center gap-2 text-white border rounded-lg shadow-lg transition-all active:scale-[0.98] disabled:opacity-70 uppercase tracking-widest ${task ? 'bg-blue-600 border-blue-700 shadow-blue-500/20' : 'bg-purple-600 border-purple-700 shadow-purple-500/20'}`}
          >
            {loading ? <RotateCw className="animate-spin" size={16} /> : <Send size={16} />} 
            {task ? 'Update Task' : 'Create Task'}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
